import { FunctionComponent, ReactNode } from 'react'

import styled, { css } from 'styled-components'

import { SportEntityType } from '@utils/entities'
import Blink from '@components/Blink'
import TeamLogo from '@components/LayoutTabs/SubsectionReusableComponents/TeamLogo'

type SportEntityProps = {
  name: string
  image: {
    src: string
    alt: string
  }
  link?: SportEntityType['link']
  relatedEntities?: SportEntityType['relatedEntities']
  subtitle?: SportEntityType['subtitle']
  children?: ReactNode
}

const StyledListSportEntity = styled.li`
  ${({
    theme: {
      spacing: { spacing8 },
      colors: { strokeWeak },
    },
  }) => css`
    padding: ${spacing8} 0;
    border-bottom: 1px solid ${strokeWeak};
    display: flex;
    gap: ${spacing8};
    align-items: center;
    min-height: 48px;
  `}
`

const StyledSportEntityText = styled.div`
  ${({
    theme: {
      colors: { textDefault },
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadCSS },
        },
      },
    },
  }) => css`
    color: ${textDefault};
    ${small1SubheadCSS};
  `}
`

const StyledClickableTeamWrapper = styled(Blink)`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    appearance: none;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: ${spacing8};
  `}
`

const StyledRelatedEntity = styled.div`
  ${({
    theme: {
      colors: { textWeak },
      typography: {
        catchword: {
          small1: { bundledCSS: small1catchwordCSS },
        },
      },
    },
  }) => css`
    color: ${textWeak};
    ${small1catchwordCSS};
  `}
`

const TextWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing4 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing4};
  `}
`

const SportEntity: FunctionComponent<SportEntityProps> = ({
  image,
  name,
  link,
  relatedEntities,
  subtitle,
  children,
}) => {
  const mainRelatedEntity = relatedEntities?.[0]
  return (
    <StyledListSportEntity>
      {link ? (
        <StyledClickableTeamWrapper {...link}>
          <TeamLogo name={image?.alt} url={image?.src} size={40} />
          <TextWrapper>
            <StyledSportEntityText>{name}</StyledSportEntityText>
            {(mainRelatedEntity || subtitle) && (
              <StyledRelatedEntity>
                {mainRelatedEntity?.title ?? subtitle}
              </StyledRelatedEntity>
            )}
          </TextWrapper>
        </StyledClickableTeamWrapper>
      ) : (
        <>
          <TeamLogo name={image?.alt} url={image?.src} size={40} />
          <TextWrapper>
            <StyledSportEntityText>{name}</StyledSportEntityText>
            {(mainRelatedEntity || subtitle) && (
              <StyledRelatedEntity>
                {mainRelatedEntity?.title ?? subtitle}
              </StyledRelatedEntity>
            )}
          </TextWrapper>
        </>
      )}
      {children}
    </StyledListSportEntity>
  )
}

export default SportEntity
